import type { FC } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { useSnackbar } from 'notistack';

import { actions as customersActions } from 'src/services/state/Customers';
import Button from 'src/components/Button/Button';
import TextField from 'src/components/TextField/TextField';

type Step = 'question' | 'phone' | 'code';

interface SmsNotificationPhoneDialogProps {
  customerId: number | string;
  /** Number prefilled in the phone step, e.g. an unconfirmed or mobile number. */
  initialPhone: string;
  onClose: () => void;
  /** Skip the yes/no question, e.g. when changing an already confirmed number. */
  startWithPhone?: boolean;
}

const getErrorMessage = (error: any, fallback: string): string => {
  const data = error?.response?.data;
  return (
    data?.detail ||
    data?.smsNotificationPhone?.[0] ||
    data?.code?.[0] ||
    data?.nonFieldErrors?.[0] ||
    data?.[0] ||
    fallback
  );
};

const SmsNotificationPhoneDialog: FC<SmsNotificationPhoneDialogProps> = ({
  customerId,
  initialPhone,
  onClose,
  startWithPhone = false,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  // Mounted only while open (see AppointmentRecordForm), so the initial state
  // is taken from the props once and not reset when the customer is updated.
  const [step, setStep] = useState<Step>(startWithPhone ? 'phone' : 'question');
  const [phone, setPhone] = useState(initialPhone);
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const run = async (request: () => Promise<void>, fallback: string) => {
    setIsSaving(true);
    setError('');
    try {
      await request();
    } catch (requestError: any) {
      setError(getErrorMessage(requestError, fallback));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDecline = () =>
    run(async () => {
      await customersActions.updateSmsNotificationPhone(customerId, '');
      enqueueSnackbar(
        t('AppointmentRecordForm.SmsNotificationPhone.successMessage'),
        { variant: 'success' },
      );
      onClose();
    }, t('AppointmentRecordForm.SmsNotificationPhone.errorMessage'));

  const handleSendCode = () => {
    if (!phone.trim()) {
      setError(t('AppointmentRecordForm.SmsNotificationPhone.required'));
      return;
    }
    run(async () => {
      await customersActions.sendSmsVerificationCode(customerId, phone);
      setCode('');
      setStep('code');
    }, t('AppointmentRecordForm.SmsNotificationPhone.errorMessage'));
  };

  const handleVerify = () => {
    if (!code.trim()) {
      setError(t('AppointmentRecordForm.SmsNotificationPhone.codeRequired'));
      return;
    }
    run(async () => {
      await customersActions.verifySmsVerificationCode(customerId, code);
      enqueueSnackbar(
        t('AppointmentRecordForm.SmsNotificationPhone.verifiedMessage'),
        { variant: 'success' },
      );
      onClose();
    }, t('AppointmentRecordForm.SmsNotificationPhone.errorMessage'));
  };

  const descriptions: Record<Step, string> = {
    code: t('AppointmentRecordForm.SmsNotificationPhone.codeDescription', {
      phone,
    }),
    phone: t('AppointmentRecordForm.SmsNotificationPhone.phoneDescription'),
    question: t('AppointmentRecordForm.SmsNotificationPhone.question'),
  };

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      onClose={isSaving ? undefined : onClose}
      open
    >
      <DialogTitle>
        {t('AppointmentRecordForm.SmsNotificationPhone.title')}
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: step === 'question' ? 0 : 2 }}>
          {descriptions[step]}
        </DialogContentText>
        {step === 'phone' && (
          <TextField
            autoFocus
            disabled={isSaving}
            error={!!error}
            fullWidth
            helperText={error || undefined}
            inputProps={{ inputMode: 'tel' }}
            label={t('AppointmentRecordForm.SmsNotificationPhone.inputLabel')}
            onChange={(event) => {
              setPhone(event.target.value);
              setError('');
            }}
            type="tel"
            value={phone}
            variant="outlined"
          />
        )}
        {step === 'code' && (
          <TextField
            autoFocus
            disabled={isSaving}
            error={!!error}
            fullWidth
            helperText={error || undefined}
            inputProps={{
              autoComplete: 'one-time-code',
              inputMode: 'numeric',
              maxLength: 4,
            }}
            label={t('AppointmentRecordForm.SmsNotificationPhone.codeLabel')}
            onChange={(event) => {
              setCode(event.target.value);
              setError('');
            }}
            value={code}
            variant="outlined"
          />
        )}
        {step === 'question' && error && (
          <DialogContentText color="error" sx={{ mt: 2 }}>
            {error}
          </DialogContentText>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          '& > :not(style) ~ :not(style)': {
            marginLeft: { sm: 1, xs: 0 },
          },
          'alignItems': { sm: 'center', xs: 'stretch' },
          'flexDirection': { sm: 'row', xs: 'column' },
          'flexWrap': 'wrap',
          'gap': { sm: 0, xs: 1 },
        }}
      >
        {step === 'question' && (
          <>
            <Button disabled={isSaving} onClick={() => setStep('phone')}>
              {t('AppointmentRecordForm.SmsNotificationPhone.accept')}
            </Button>
            <Button
              color="secondary"
              disabled={isSaving}
              onClick={handleDecline}
            >
              {t('AppointmentRecordForm.SmsNotificationPhone.decline')}
            </Button>
            <Button disabled={isSaving} onClick={onClose}>
              {t('General.abort')}
            </Button>
          </>
        )}
        {step === 'phone' && (
          <>
            {!startWithPhone && (
              <Button
                disabled={isSaving}
                onClick={() => {
                  setError('');
                  setStep('question');
                }}
              >
                {t('General.back')}
              </Button>
            )}
            {startWithPhone && (
              <Button
                color="secondary"
                disabled={isSaving}
                onClick={handleDecline}
              >
                {t('AppointmentRecordForm.SmsNotificationPhone.decline')}
              </Button>
            )}
            <Button disabled={isSaving} onClick={onClose}>
              {t('General.abort')}
            </Button>
            <Button loading={isSaving} onClick={handleSendCode}>
              {t('AppointmentRecordForm.SmsNotificationPhone.sendCode')}
            </Button>
          </>
        )}
        {step === 'code' && (
          <>
            <Button
              disabled={isSaving}
              onClick={() => {
                setError('');
                setStep('phone');
              }}
            >
              {t('AppointmentRecordForm.SmsNotificationPhone.changePhone')}
            </Button>
            <Button disabled={isSaving} onClick={handleSendCode}>
              {t('AppointmentRecordForm.SmsNotificationPhone.resendCode')}
            </Button>
            <Button disabled={isSaving} onClick={onClose}>
              {t('AppointmentRecordForm.SmsNotificationPhone.later')}
            </Button>
            <Button loading={isSaving} onClick={handleVerify}>
              {t('AppointmentRecordForm.SmsNotificationPhone.verify')}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SmsNotificationPhoneDialog;
